import { CloseIcon } from "@chakra-ui/icons";
import { Button, Flex, Icon, Image, useColorMode } from "@chakra-ui/react";
import useCustomChakraTheme from "hooks/use-custom-chakra-theme";
import React, { useEffect, useState } from "react";

interface TemplateAttachmentProps {
  mediaUrl: string | null;
  mediaType: string | null;
  file?: File | null;
  onAttachmentRemove?: () => void;
}

const SUPPORTED_MEDIA_TYPES = [
  "image/jpeg",
  "image/png",
  "video/mp4",
  "audio/mpeg",
  "audio/ogg; codecs=opus",
  "application/pdf",
  "image/gif",
];

const TemplateAttachmentWrapper = ({
  children,
  onRemove,
}: {
  children: React.ReactNode;
  onRemove?: () => void;
}) => {
  const { colorMode } = useColorMode();
  const { colorScheme } = useCustomChakraTheme();

  return (
    <Flex
      borderRadius="lg"
      pr={8}
      pt={8}
      position="relative"
      width="fit-content"
      mb={8}
      overflow="hidden"
      maxWidth="600px"
    >
      {typeof onRemove !== "undefined" && (
        <Button
          position="absolute"
          size="xs"
          top={0}
          right={0}
          mt={2}
          mr={2}
          variant="ghost"
          colorScheme={colorScheme}
          onClick={() => {
            if (typeof onRemove === "undefined") {
              return;
            }

            onRemove();
          }}
        >
          <Icon as={CloseIcon} />
        </Button>
      )}
      {children}
    </Flex>
  );
};

const TemplateAttachment = ({
  mediaUrl,
  mediaType,
  file,
  onAttachmentRemove,
}: TemplateAttachmentProps) => {
  if (!mediaUrl && !file) {
    return null;
  }

  if (mediaUrl && mediaType && !SUPPORTED_MEDIA_TYPES.includes(mediaType)) {
    return null;
  }

  if (file && !SUPPORTED_MEDIA_TYPES.includes(file.type)) {
    return null;
  }

  const actualMediaType = file ? file.type : mediaType;

  const [actualMediaUrl, setActualMediaUrl] = useState<string | null>(mediaUrl);

  useEffect(() => {
    let fileReader: FileReader | null = null;
    let isCancel = false;

    if (file) {
      fileReader = new FileReader();
      fileReader.onload = () => {
        if (fileReader!.result && !isCancel) {
          setActualMediaUrl(fileReader!.result as string);
        }
      };
      fileReader.readAsDataURL(file);
    } else {
      setActualMediaUrl(mediaUrl);
    }

    return () => {
      isCancel = true;

      if (fileReader && fileReader.readyState === 1) {
        fileReader.abort();
      }
    };
  }, [file, mediaUrl, mediaType]);

  if (actualMediaType!.includes("image")) {
    return (
      <TemplateAttachmentWrapper onRemove={onAttachmentRemove}>
        <Image
          boxSize="sm"
          objectFit="cover"
          src={actualMediaUrl!}
          alt="Template attachment"
          style={{ maxWidth: "100%", maxHeight: "300px" }}
          borderRadius="lg"
        />
      </TemplateAttachmentWrapper>
    );
  }

  if (actualMediaType!.includes("video")) {
    return (
      <TemplateAttachmentWrapper onRemove={onAttachmentRemove}>
        <video
          src={actualMediaUrl!}
          controls
          style={{
            maxWidth: "500px",
            maxHeight: "300px",
            borderRadius: "0.5rem",
          }}
        />
      </TemplateAttachmentWrapper>
    );
  }

  if (actualMediaType!.includes("audio")) {
    return (
      <TemplateAttachmentWrapper onRemove={onAttachmentRemove}>
        <audio
          src={actualMediaUrl!}
          controls
          style={{ maxWidth: "500px", borderRadius: "0.5rem" }}
        >
          Your browser does not support the
          <code>audio</code> element.
        </audio>
      </TemplateAttachmentWrapper>
    );
  }

  if (actualMediaType!.includes("pdf")) {
    return (
      <TemplateAttachmentWrapper onRemove={onAttachmentRemove}>
        <a
          href={actualMediaUrl!}
          target="_blank"
          rel="noopener noreferrer"
          download={true}
          style={{
            overflow: "hidden",
            whiteSpace: "nowrap",
            textOverflow: "ellipsis",
          }}
        >
          {file ? file.name : "PDF Attachment"}
        </a>
      </TemplateAttachmentWrapper>
    );
  }

  return null;
};

export default TemplateAttachment;
